import { createFileRoute } from '@tanstack/react-router';
import { copilotStatus, flowSnapshot, productionsList } from '@/api/resources';
import { warm, warmBackground } from '@/api/loaders';
import { requireSession } from '@/lib/route-guards';
import { DP_NODE_IDS } from '@/lib/flow-layout';
import type { DpNodeId } from '@/lib/flow-layout';
import { DataPipelineView } from '@/screens/data-pipeline';
import { searchEnum } from '@/lib/search-enum';

// LIVE and HISTORY are two different screens sharing a frame — different
// queries, different controls. Which one you are looking at belongs in the URL.
const pipelineMode = searchEnum('mode', ['live', 'history'] as const, 'live');

/** A production id's shape: a catalog id is a UUID or a short slug. The screen drops one
 *  the catalog does not hold once the catalog has loaded. */
const PRODUCTION_ID = /^[A-Za-z0-9][A-Za-z0-9/_-]{0,79}$/;

export interface DataPipelineSearch {
  trace?: string;
  mode: 'live' | 'history';
  /** The production filter; absent = every production (unrestricted users only). */
  production?: string;
  /** The diagram node whose card is open. */
  node?: DpNodeId;
}

export const Route = createFileRoute('/backend/data-pipeline')({
  beforeLoad: requireSession,
  // `?trace=web:…|tkt:…` deep-links the E2E lineage TRACE mode (shareable); the
  // production filter and the open card live in the URL too, so a reload or a
  // shared link shows the same page.
  validateSearch: (s: Record<string, unknown>): DataPipelineSearch => ({
    trace: typeof s.trace === 'string' && s.trace ? s.trace : undefined,
    mode: pipelineMode.parse(s),
    production:
      typeof s.production === 'string' && PRODUCTION_ID.test(s.production) ? s.production : undefined,
    node: DP_NODE_IDS.includes(s.node as DpNodeId) ? (s.node as DpNodeId) : undefined,
  }),
  loaderDeps: ({ search }) => ({ production: search.production ?? null }),
  // Search-driven route: no skeleton for an in-screen navigation (router.ts).
  pendingMs: Infinity,
  // The production filter's catalog and the diagram's snapshot for the selected
  // scope. Feeds, history and lineage stay volatile and mount with the screen.
  loader: ({ context, deps }) => {
    warmBackground(context.queryClient, copilotStatus(undefined), flowSnapshot(deps?.production ?? null));
    return warm(context.queryClient, productionsList({}));
  },
  component: Page,
});

function Page() {
  const { trace, mode, production, node } = Route.useSearch();
  return <DataPipelineView trace={trace} mode={mode} production={production} node={node} />;
}
